import React, { useState, useEffect, useRef } from 'react';
import { Box, Alert, Typography, IconButton, Button } from '@mui/material';
import { Close as CloseIcon, Security as SecurityIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const TwoFactorReminderBanner = ({ onSetup2FA }) => {
  // should_show_2fa_reminder comes back on User.to_dict(), which both
  // AuthContext's /auth/check-auth (populating currentUser) and this
  // component's own /auth/profile call returned - this used to fire its
  // own duplicate /auth/profile request on mount purely to re-fetch a
  // field already available on currentUser, doubling this request on every
  // authenticated page load (this banner is mounted app-wide). Read it off
  // currentUser instead.
  const { currentUser, loading: authLoading } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const navigate = useNavigate();
  // Only ever evaluate once currentUser first becomes available - mirrors
  // the old "fetch once on mount" behavior instead of re-evaluating (and
  // potentially re-showing a dismissed banner) every time currentUser is
  // replaced with a new object elsewhere in the app.
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    if (hasCheckedRef.current || authLoading || !currentUser) {
      return;
    }
    hasCheckedRef.current = true;

    if (currentUser.should_show_2fa_reminder === true) {
      setShowBanner(true);
    }
  }, [currentUser, authLoading]);

  const handleCloseBanner = () => {
    setShowBanner(false);
    // Note: We don't store dismissal in localStorage so it will reappear on next login
  };

  const handleSetup2FA = () => {
    if (onSetup2FA) {
      // If callback is provided, use it to directly open 2FA setup
      onSetup2FA();
      setShowBanner(false); // Close the banner when setup is initiated
    } else {
      // Fallback to navigating to settings page
      navigate('/settings');
    }
  };

  if (authLoading || !showBanner) {
    return null;
  }

  return (
    <Alert
      severity="info"
      sx={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1300,
        maxWidth: '90vw',
        width: '600px',
        boxShadow: 3,
        borderRadius: 2,
        '& .MuiAlert-message': {
          width: '100%'
        }
      }}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSetup2FA}
            startIcon={<SecurityIcon />}
            sx={{ 
              minWidth: '140px',
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Setup 2FA
          </Button>
          <IconButton
            size="small"
            onClick={handleCloseBanner}
            sx={{ 
              color: 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      }
    >
      <Box>
        <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
          Secure Your Account
        </Typography>
        <Typography variant="body2">
          Enhance your account security by enabling two-factor authentication. This adds an extra layer of protection to keep your financial data safe.
        </Typography>
      </Box>
    </Alert>
  );
};

export default TwoFactorReminderBanner; 